import { jsx as y } from "react/jsx-runtime";
import { createContext as F, useContext as G, useState as M, useEffect as I, useMemo as b, useCallback as V } from "react";
import { theme as z, ConfigProvider as W, App as $ } from "antd";
import { brand as t, gray as e, border as x, surface as B, shadows as k, text as L, overlay as j, semantic as r, brandAlpha as h } from "@digiwedge/ui-tokens/colors";
import { radii as c, spacing as a } from "@digiwedge/ui-tokens/spacing";
import { fontSize as s } from "@digiwedge/ui-tokens/typography";
import { duration as D } from "@digiwedge/ui-tokens/motion";
import K from "antd/locale/en_US.js";
import Y from "antd/locale/fr_FR.js";
const d = {
  token: {
    // =========================================================================
    // Brand Colors
    // =========================================================================
    colorPrimary: t.primary,
    colorPrimaryBg: t.primaryLight,
    colorLink: t.primary,
    colorLinkHover: t.primary,
    // =========================================================================
    // Semantic Colors
    // =========================================================================
    colorInfo: r.info,
    colorInfoBg: r.infoLight,
    colorInfoText: r.infoText,
    colorInfoTextHover: r.infoText,
    colorInfoTextActive: r.infoText,
    colorSuccess: r.success,
    colorSuccessBg: r.successLight,
    colorSuccessText: r.successText,
    colorSuccessTextHover: r.successText,
    colorSuccessTextActive: r.successText,
    colorWarning: r.warning,
    colorWarningBg: r.warningLight,
    colorWarningText: r.warningText,
    colorWarningTextHover: r.warningText,
    colorWarningTextActive: r.warningText,
    colorError: r.error,
    colorErrorBg: r.errorLight,
    colorErrorText: r.errorText,
    colorErrorTextHover: r.errorText,
    colorErrorTextActive: r.errorText,
    // =========================================================================
    // Surface & Background Colors
    // =========================================================================
    colorBgContainer: B.surface,
    colorBgElevated: B.surface,
    colorBgLayout: B.background,
    colorBgSpotlight: e[900],
    colorBgMask: j.modal,
    // =========================================================================
    // Text Colors
    // =========================================================================
    colorText: L.primary,
    colorTextSecondary: L.secondary,
    colorTextTertiary: L.muted,
    colorTextQuaternary: L.placeholder,
    // =========================================================================
    // Border Colors
    // =========================================================================
    colorBorder: x.outline,
    colorBorderSecondary: x.border,
    colorSplit: x.divider,
    // =========================================================================
    // Fill Colors (for backgrounds of interactive elements)
    // =========================================================================
    colorFill: e[100],
    colorFillSecondary: e[50],
    colorFillTertiary: e[200],
    colorFillQuaternary: e[100],
    // =========================================================================
    // Typography
    // =========================================================================
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: s.md,
    // 14px - base size
    fontSizeHeading1: s["5xl"],
    // 36px
    fontSizeHeading2: s["4xl"],
    // 30px
    fontSizeHeading3: s["3xl"],
    // 24px
    fontSizeHeading4: s["2xl"],
    // 20px
    fontSizeHeading5: s.xl,
    // 18px
    fontSizeSM: s.sm,
    // 12px
    fontSizeLG: s.lg,
    // 16px
    fontSizeXL: s.xl,
    // 18px
    lineHeight: 1.5,
    lineHeightHeading1: 1.22,
    // Tighter for large headings
    lineHeightHeading2: 1.27,
    lineHeightHeading3: 1.33,
    lineHeightHeading4: 1.4,
    lineHeightHeading5: 1.44,
    lineHeightLG: 1.5,
    lineHeightSM: 1.5,
    // =========================================================================
    // Spacing & Sizing
    // =========================================================================
    padding: a.md,
    // 12px
    paddingXS: a.xs,
    // 4px
    paddingSM: a.sm,
    // 8px
    paddingLG: a.lg,
    // 16px
    paddingXL: a.xl,
    // 24px
    margin: a.lg,
    // 16px
    marginXS: a.xs,
    // 4px
    marginSM: a.sm,
    // 8px
    marginLG: a.xl,
    // 24px
    marginXL: a.xxl,
    // 32px
    controlHeight: 36,
    // Base control height
    controlHeightSM: 28,
    controlHeightLG: 44,
    // Meets touch target minimum
    controlHeightXS: 24,
    // =========================================================================
    // Border Radius
    // =========================================================================
    borderRadius: c.md,
    // 8px - default
    borderRadiusSM: c.sm,
    // 4px
    borderRadiusLG: c.lg,
    // 12px
    borderRadiusXS: 2,
    // =========================================================================
    // Motion & Animation
    // =========================================================================
    motionDurationFast: `${D.fast / 1e3}s`,
    // 0.15s
    motionDurationMid: `${D.normal / 1e3}s`,
    // 0.25s
    motionDurationSlow: `${D.slow / 1e3}s`,
    // 0.35s
    motionEaseInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    motionEaseOut: "cubic-bezier(0, 0, 0.2, 1)",
    motionEaseOutBack: "cubic-bezier(0.34, 1.56, 0.64, 1)",
    // Spring-like overshoot
    // =========================================================================
    // Shadows
    // =========================================================================
    boxShadow: k.lightPrimary,
    boxShadowSecondary: k.lightSecondary,
    // =========================================================================
    // Z-Index Scale
    // =========================================================================
    zIndexBase: 0,
    zIndexPopupBase: 1e3,
    // =========================================================================
    // Wire Frame Mode (disabled)
    // =========================================================================
    wireframe: !1
  },
  components: {
    // =========================================================================
    // Button Customizations
    // =========================================================================
    Button: {
      colorPrimary: t.primary,
      colorPrimaryHover: t.primaryHover,
      colorPrimaryActive: t.primaryActive,
      borderRadius: c.md,
      controlHeight: 36,
      controlHeightSM: 28,
      controlHeightLG: 44,
      paddingInline: a.lg,
      paddingInlineSM: a.md,
      paddingInlineLG: a.xl
    },
    // =========================================================================
    // Input Customizations
    // =========================================================================
    Input: {
      colorBorder: x.outline,
      colorBgContainer: B.surface,
      borderRadius: c.md,
      controlHeight: 36,
      controlHeightSM: 28,
      controlHeightLG: 44,
      paddingInline: a.md
    },
    // =========================================================================
    // Select Customizations
    // =========================================================================
    Select: {
      colorBorder: x.outline,
      borderRadius: c.md,
      controlHeight: 36,
      controlHeightSM: 28,
      controlHeightLG: 44
    },
    // =========================================================================
    // Card Customizations
    // =========================================================================
    Card: {
      colorBgContainer: B.surface,
      borderRadiusLG: c.lg,
      paddingLG: a.xl
    },
    // =========================================================================
    // Table Customizations
    // =========================================================================
    Table: {
      colorBgContainer: B.surface,
      borderColor: x.border,
      headerBg: e[50],
      rowHoverBg: t.primaryLight
    },
    // =========================================================================
    // Menu Customizations
    // =========================================================================
    Menu: {
      itemSelectedBg: t.primaryLight,
      itemSelectedColor: t.primary,
      borderRadius: c.md
    },
    // =========================================================================
    // Modal Customizations
    // =========================================================================
    Modal: {
      borderRadiusLG: c.lg
    },
    // =========================================================================
    // Alert Customizations
    // =========================================================================
    Alert: {
      borderRadiusLG: c.md
    },
    // =========================================================================
    // Tag Customizations
    // =========================================================================
    Tag: {
      borderRadiusSM: c.sm
    },
    // =========================================================================
    // Tooltip Customizations
    // =========================================================================
    Tooltip: {
      borderRadius: c.sm
    }
  }
}, N = {
  algorithm: z.darkAlgorithm,
  token: {
    // Inherit all light theme tokens
    ...d.token,
    // =========================================================================
    // Dark Mode Surface Overrides
    // =========================================================================
    colorBgContainer: e[800],
    colorBgElevated: e[700],
    colorBgLayout: e[900],
    colorBgSpotlight: e[700],
    // =========================================================================
    // Dark Mode Text Overrides
    // =========================================================================
    colorText: e[50],
    colorTextSecondary: e[300],
    colorTextTertiary: e[400],
    colorTextQuaternary: e[500],
    // =========================================================================
    // Dark Mode Border Overrides
    // =========================================================================
    colorBorder: e[600],
    colorBorderSecondary: e[700],
    colorSplit: e[700],
    // =========================================================================
    // Dark Mode Fill Overrides
    // =========================================================================
    colorFill: e[700],
    colorFillSecondary: e[800],
    colorFillTertiary: e[600],
    colorFillQuaternary: e[700],
    // =========================================================================
    // Dark Mode Primary Background
    // =========================================================================
    colorPrimaryBg: h.primary15,
    // =========================================================================
    // Dark Mode Semantic Backgrounds
    // =========================================================================
    colorSuccessBg: r.successBgDark,
    colorSuccessBgHover: r.successBgDark,
    colorSuccessBorder: r.successBorderDark,
    colorSuccessBorderHover: r.successBorderDark,
    colorSuccessText: r.successTextDark,
    colorSuccessTextHover: r.successTextDark,
    colorSuccessTextActive: r.successTextDark,
    colorWarningBg: r.warningBgDark,
    colorWarningBgHover: r.warningBgDark,
    colorWarningBorder: r.warningBorderDark,
    colorWarningBorderHover: r.warningBorderDark,
    colorWarningText: r.warningTextDark,
    colorWarningTextHover: r.warningTextDark,
    colorWarningTextActive: r.warningTextDark,
    colorErrorBg: r.errorBgDark,
    colorErrorBgHover: r.errorBgDark,
    colorErrorBorder: r.errorBorderDark,
    colorErrorBorderHover: r.errorBorderDark,
    colorErrorText: r.errorTextDark,
    colorErrorTextHover: r.errorTextDark,
    colorErrorTextActive: r.errorTextDark,
    colorInfoBg: r.infoBgDark,
    colorInfoBgHover: r.infoBgDark,
    colorInfoBorder: r.infoBorderDark,
    colorInfoBorderHover: r.infoBorderDark,
    colorInfoText: r.infoTextDark,
    colorInfoTextHover: r.infoTextDark,
    colorInfoTextActive: r.infoTextDark,
    // =========================================================================
    // Dark Mode Shadows
    // =========================================================================
    boxShadow: k.darkPrimary,
    boxShadowSecondary: k.darkSecondary
  },
  components: {
    // Inherit light component tokens and override where needed
    ...d.components,
    // =========================================================================
    // Button Dark Mode
    // =========================================================================
    Button: {
      ...d.components?.Button
    },
    // =========================================================================
    // Input Dark Mode
    // =========================================================================
    Input: {
      ...d.components?.Input,
      colorBgContainer: e[800],
      colorBorder: e[600]
    },
    // =========================================================================
    // Select Dark Mode
    // =========================================================================
    Select: {
      ...d.components?.Select,
      colorBorder: e[600]
    },
    // =========================================================================
    // Card Dark Mode
    // =========================================================================
    Card: {
      ...d.components?.Card,
      colorBgContainer: e[800]
    },
    // =========================================================================
    // Table Dark Mode
    // =========================================================================
    Table: {
      ...d.components?.Table,
      colorBgContainer: e[800],
      borderColor: e[700],
      headerBg: e[900],
      rowHoverBg: h.primary10
    },
    // =========================================================================
    // Menu Dark Mode
    // =========================================================================
    Menu: {
      ...d.components?.Menu,
      itemSelectedBg: h.primary15,
      itemSelectedColor: t.primary
    }
  }
}, w = {
  en_US: K,
  fr_FR: Y
}, O = F({
  locale: "en_US",
  setLocale: () => {
  }
}), A = "antd-locale", q = ({ children: o, initialLocale: m }) => {
  const [l, g] = M(m ?? "en_US");
  I(() => {
    if (m) return;
    let n = null;
    try {
      n = localStorage.getItem(A);
    } catch (p) {
      console.error("Error accessing localStorage:", p);
    }
    n && w[n] && g(n);
  }, [m]);
  const v = (n) => {
    g(n);
    try {
      localStorage.setItem(A, n);
    } catch (p) {
      console.error("Error writing localStorage:", p);
    }
  }, f = b(() => ({ locale: l, setLocale: v }), [l]);
  return /* @__PURE__ */ y(O.Provider, { value: f, children: /* @__PURE__ */ y(W, { locale: w[l], children: o }) });
}, sr = () => G(O), X = F({
  mode: "light",
  toggleTheme: () => {
  }
}), P = "theme-mode", R = "theme-mode-manual", J = (o) => ({
  "--app-theme-bg-layout": o.colorBgLayout,
  "--app-theme-bg-container": o.colorBgContainer,
  "--app-theme-bg-elevated": o.colorBgElevated,
  "--app-theme-border": o.colorBorder,
  "--app-theme-border-secondary": o.colorBorderSecondary,
  "--app-theme-split": o.colorSplit,
  "--app-theme-text": o.colorText,
  "--app-theme-text-secondary": o.colorTextSecondary,
  "--app-theme-text-tertiary": o.colorTextTertiary,
  "--app-theme-primary": o.colorPrimary,
  "--app-theme-primary-bg": o.colorPrimaryBg,
  "--app-theme-success": o.colorSuccess,
  "--app-theme-success-bg": o.colorSuccessBg,
  "--app-theme-success-border": o.colorSuccessBorder,
  "--app-theme-success-text": o.colorSuccessText,
  "--app-theme-warning": o.colorWarning,
  "--app-theme-warning-bg": o.colorWarningBg,
  "--app-theme-warning-border": o.colorWarningBorder,
  "--app-theme-warning-text": o.colorWarningText,
  "--app-theme-error": o.colorError,
  "--app-theme-error-bg": o.colorErrorBg,
  "--app-theme-error-border": o.colorErrorBorder,
  "--app-theme-error-text": o.colorErrorText,
  "--app-theme-fill-secondary": o.colorFillSecondary,
  "--app-theme-fill-tertiary": o.colorFillTertiary,
  "--app-theme-focus-ring": o.colorPrimaryBorder,
  "--app-theme-interactive-hover": o.colorFillTertiary
}), dr = ({ children: o, initialTheme: m }) => {
  const [l, g] = M("light"), [v, f] = M(!1);
  I(() => {
    let i;
    try {
      if (m)
        g(m), f(!0);
      else {
        const H = localStorage.getItem(R) === "1";
        f(H);
        const Q = localStorage.getItem(P);
        if (H)
          g(Q === "dark" ? "dark" : "light");
        else {
          const u = window.matchMedia("(prefers-color-scheme: dark)"), T = () => g(u.matches ? "dark" : "light");
          T(), typeof u.addEventListener == "function" ? (u.addEventListener("change", T), i = () => u.removeEventListener("change", T)) : typeof u.addListener == "function" && (u.addListener(T), i = () => u.removeListener(T));
        }
      }
    } catch (S) {
      console.error("Error initializing theme mode:", S);
    }
    return i;
  }, [m]);
  const n = V(() => {
    f(!0), g((i) => i === "light" ? "dark" : "light");
  }, []);
  I(() => {
    try {
      localStorage.setItem(P, l), localStorage.setItem(R, v ? "1" : "0");
    } catch (i) {
      console.error("Error persisting theme mode:", i);
    }
  }, [l, v]);
  const p = b(() => l === "light" ? d : N, [l]), C = b(() => z.getDesignToken(p), [p]), E = b(() => J(C), [C]), U = b(() => ({ mode: l, toggleTheme: n }), [l, n]);
  return I(() => {
    if (typeof document > "u")
      return;
    const i = document.documentElement;
    return Object.entries(E).forEach(([S, H]) => {
      i.style.setProperty(S, H);
    }), () => {
      Object.keys(E).forEach((S) => {
        i.style.removeProperty(S);
      });
    };
  }, [E]), /* @__PURE__ */ y(q, { children: /* @__PURE__ */ y(X.Provider, { value: U, children: /* @__PURE__ */ y(W, { theme: p, children: /* @__PURE__ */ y($, { children: o }) }) }) });
}, mr = () => G(X), _ = {
  borderRadius: c.md,
  itemBg: "transparent",
  itemHoverBg: h.primary10,
  itemHoverColor: t.primary,
  itemSelectedBg: t.primaryLight,
  itemSelectedColor: t.primary,
  subMenuItemBg: "transparent",
  subMenuItemSelectedColor: t.primary
}, Z = {
  ..._,
  itemSelectedBg: h.primary15,
  darkGroupTitleColor: e[400],
  darkItemBg: "transparent",
  darkItemColor: e[300],
  darkItemDisabledColor: "rgba(249, 250, 251, 0.25)",
  darkItemHoverBg: h.primary10,
  darkItemHoverColor: e[50],
  darkItemSelectedBg: h.primary15,
  darkItemSelectedColor: t.primary,
  darkPopupBg: e[800],
  darkSubMenuItemBg: "transparent"
};
function gr(o) {
  return { ...o === "dark" ? Z : _ };
}
export {
  dr as AppThemeProvider,
  q as LocaleProvider,
  gr as createAdminSidebarMenuTokens,
  N as dark,
  d as light,
  sr as useLocale,
  mr as useTheme
};
//# sourceMappingURL=index.js.map
